/**
 * F-90 script import — PURE file→rows parsing and policy (no DOM, no File, no
 * Yjs). The runner (`import-script.ts`) and the UI ask this module three
 * things: what format is this file, what rows does it hold, and what caveats
 * must the confirm dialog state.
 *
 * The feature is "import a long script into the Original Script column
 * (F-28)": ONE column of text, one line per row. So a multi-column CSV is not
 * an error — it is a file whose FIRST column is the script. Dropping the other
 * columns silently would be, which is why `ImportPreview` carries the field
 * count for the dialog to say so out loud.
 */
import { parseDelimited, stripBom } from "./delimited.js";

/**
 * `.txt` is deliberately NOT parsed as delimited text: F-90 defines it as "one
 * line per row", so a line containing a comma or a tab is one row's content,
 * not two fields. Guessing otherwise would silently truncate every line of a
 * subtitle script at its first comma.
 */
export type ScriptFormat = "csv" | "tsv" | "txt";

/** Extensions the file picker and the drop target accept (F-90). */
export const IMPORT_ACCEPT_EXTENSIONS = [".txt", ".csv", ".tsv"] as const;

/**
 * The picker's `accept`. Extensions rather than MIME types: the OS reports
 * `.tsv` inconsistently (often as no type at all), so a type-based filter hides
 * exactly the files this feature exists to import.
 */
export const IMPORT_ACCEPT = IMPORT_ACCEPT_EXTENSIONS.join(",");

/**
 * Format from the FILE NAME, or undefined for anything we don't import.
 * Name-driven rather than content-sniffed on purpose: a script line legitimately
 * contains commas and tabs, so content sniffing would misread prose as a table.
 * `.tab` is accepted as a tsv alias (what some exporters emit).
 */
export function scriptFormat(filename: string): ScriptFormat | undefined {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".tsv") || lower.endsWith(".tab")) return "tsv";
  if (lower.endsWith(".txt")) return "txt";
  return undefined;
}

/** True for a file this feature can import — the drop layer's filter. */
export function isScriptFile(filename: string): boolean {
  return scriptFormat(filename) !== undefined;
}

/** What the confirm dialog reads, and what the runner writes. */
export interface ImportPreview {
  /** The values to write, in file order. Never blank, never altered. */
  rows: string[];
  /**
   * Widest record in the file (1 for `.txt`). > 1 means the file has columns
   * this import DROPS — the dialog must say so; F-90 imports one column.
   */
  fields: number;
  /** Blank/whitespace-only lines skipped (see `parseScript`). */
  blankSkipped: number;
}

/**
 * Parse file text into the rows to import.
 *
 * Two policies worth stating, because both are choices:
 *  - **Values are verbatim.** Never trimmed, never normalized. The text is the
 *    translation source: its hash is what C5's guard re-checks and what the
 *    translation memory keys on, so quietly editing it here would desync both.
 *  - **Whitespace-only lines are skipped**, and counted. A script's blank lines
 *    are paragraph separators, and importing them as empty rows would add rows
 *    that translation skips (F-70: empty source ⇒ empty target) and that F-26
 *    renders as slivers — noise the user did not ask for. The count is
 *    surfaced rather than swallowed.
 */
export function parseScript(text: string, format: ScriptFormat): ImportPreview {
  const records = format === "txt" ? txtRecords(text) : parseDelimited(text, delimiterFor(format));

  const rows: string[] = [];
  let fields = 1;
  let blankSkipped = 0;
  for (const record of records) {
    fields = Math.max(fields, record.length);
    const value = record[0] ?? "";
    if (value.trim() === "") {
      // A record that is entirely empty is a blank line. One with an empty
      // FIRST field but content further along (",,x") is also skipped: we
      // import column 1, and column 1 is blank.
      blankSkipped++;
      continue;
    }
    rows.push(value);
  }
  return { rows, fields, blankSkipped };
}

function delimiterFor(format: "csv" | "tsv"): string {
  return format === "csv" ? "," : "\t";
}

/**
 * `.txt`: one line per row, \n / \r\n / lone \r, BOM stripped.
 *
 * The final newline is dropped rather than counted as a blank line — every
 * editor ends a file with one, so counting it would report "1 blank line
 * skipped" on a file that has none.
 */
function txtRecords(text: string): string[][] {
  const lines = stripBom(text).split(/\r\n|\r|\n/);
  if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
  return lines.map((line) => [line]);
}
